import { QueryClient } from "@tanstack/react-query";
import {
  persistQueryClient,
  type PersistedClient,
  type Persister,
} from "@tanstack/react-query-persist-client";
import { db } from "./dexie-db";
import { APP_VERSION } from "./api-base";

/**
 * TanStack Query client + dexie-backed persistence.
 *
 *  - staleTime 60s default (dashboard route überschreibt für 5min)
 *  - gcTime 24h damit cached daten nach reload sofort sichtbar sind
 *  - retry: nur 1× bei mutations, 2× bei queries (offline-flow ist outbox)
 *  - persister: schreibt in dexie.kv['react-query']; buster bound to APP_VERSION
 *    → bei deploy invalidiert sich der cache automatisch
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const PERSIST_KEY = "react-query-cache";

const dexiePersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    await db.kv.put({
      key: PERSIST_KEY,
      value: client,
      updatedAt: Date.now(),
    });
  },
  restoreClient: async () => {
    const row = await db.kv.get(PERSIST_KEY);
    return row?.value as PersistedClient | undefined;
  },
  removeClient: async () => {
    await db.kv.delete(PERSIST_KEY);
  },
};

/**
 * Wird einmal in main.tsx (oder App.tsx mount) gestartet. Kann den await
 * skippen — persistence ist non-blocking, queries laufen sofort.
 */
export function startQueryPersistence(): void {
  void persistQueryClient({
    queryClient,
    persister: dexiePersister,
    maxAge: 24 * 60 * 60 * 1000,
    buster: APP_VERSION,
    dehydrateOptions: {
      shouldDehydrateQuery: (q) => q.state.status === "success",
    },
  });
}

/**
 * Logout-helper für AuthContext — clears query-cache UND persisted snapshot
 * gleichzeitig (sonst zeigt der nächste user fragments vom vorherigen).
 */
export async function resetQueryStateForLogout(): Promise<void> {
  queryClient.cancelQueries();
  queryClient.clear();
  await dexiePersister.removeClient();
}
